import type { components } from "@/api-schema";
import { sortByLabel } from "@/common/helpers/sort-by-label";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { Card, CardContent } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { MailIcon, PhoneIcon, PrinterIcon } from "lucide-react";
import { useEffect } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

type BrandOption = Pick<components["schemas"]["Brand"], "uuid" | "name" | "logoUrl">;

interface BrandContactValue {
  brandUuid: string;
  phone?: string;
  fax?: string;
  email?: string;
}

interface BrandRelationsFieldsProps {
  brands: BrandOption[];
}

export function BrandRelationsFields({ brands }: BrandRelationsFieldsProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const { control, getValues } = useFormContext();
  const watchedBrands = useWatch({ control, name: "brands" }) as string[] | undefined;
  const { fields, replace } = useFieldArray({ control, name: "brandRelations" });

  useEffect(() => {
    const selected = (getValues("brands") as string[] | undefined) ?? [];
    const current = (getValues("brandRelations") as BrandContactValue[] | undefined) ?? [];

    const filtered = current.filter((contact) => selected.includes(contact.brandUuid));
    const existingUuids = new Set(filtered.map((contact) => contact.brandUuid));
    const additions = selected
      .filter((brandUuid) => !existingUuids.has(brandUuid))
      .map((brandUuid) => ({ brandUuid, phone: "", fax: "", email: "" }));

    if (additions.length === 0 && filtered.length === current.length) {
      return;
    }

    replace([...filtered, ...additions]);
  }, [watchedBrands, getValues, replace]);

  if (fields.length === 0) {
    return null;
  }

  const sortedFields = sortByLabel(
    fields.map((contactField, index) => {
      const brandUuid = (contactField as typeof contactField & BrandContactValue).brandUuid;
      const brand = brands.find((item) => item.uuid === brandUuid);
      return { contactField, index, brand, brandName: brand?.name ?? brandUuid };
    }),
    ({ brandName }) => brandName,
  );

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {sortedFields.map(({ contactField, index, brand, brandName }) => {
        return (
          <Card key={contactField.id} depth={1} className="py-3">
            <CardContent className="flex flex-col gap-2 px-4">
              <div className="flex items-center gap-2 font-medium">
                <BrandLogo brand={{ name: brandName, logoUrl: brand?.logoUrl }} variant={BrandLogoVariant.COMPACT} />
                <span>{brandName}</span>
              </div>
              <div className="space-y-1">
                <FormField
                  control={control}
                  name={`brandRelations.${index}.phone`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          prefix={<PhoneIcon className="h-4 w-4" />}
                          placeholder={t("common:Phone")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`brandRelations.${index}.fax`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          prefix={<PrinterIcon className="h-4 w-4" />}
                          placeholder={t("common:Fax")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`brandRelations.${index}.email`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          type="email"
                          {...field}
                          value={field.value ?? ""}
                          prefix={<MailIcon className="h-4 w-4" />}
                          placeholder={t("common:Email")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
